import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Building2, MoreVertical, Trash2, Archive, RotateCcw, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatTRY } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MoneyInput from "@/components/MoneyInput";

const STATUS_LABEL = { planlama: "Planlama", insaat: "İnşaat", tamamlandi: "Tamamlandı" };
const STATUS_TONE = {
  planlama: "bg-secondary text-muted-foreground",
  insaat: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
  tamamlandi: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
};

export default function Projects() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("active");
  const [projects, setProjects] = useState([]);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyForm = { name: "", address: "", mahalle: "", ada: "", parsel: "", units_count: 0, budget: 0, status: "planlama", start_date: "", end_date: "" };
  const [form, setForm] = useState(emptyForm);

  const resetForm = () => { setForm(emptyForm); setEditingId(null); };
  const openCreate = () => { resetForm(); setOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      address: p.address || "",
      mahalle: p.mahalle || "",
      ada: p.ada || "",
      parsel: p.parsel || "",
      units_count: p.units_count || 0,
      budget: p.budget || 0,
      status: p.status || "planlama",
      start_date: p.start_date || "",
      end_date: p.end_date || "",
    });
    setOpen(true);
  };

  const load = async () => {
    const [{ data: active }, { data: archived }] = await Promise.all([
      api.get(`/projects?archived=false`),
      api.get(`/projects?archived=true`),
    ]);
    setProjects(active);
    setArchivedProjects(archived);
  };

  useEffect(() => {
    load();
  }, []);

  const visible =
    tab === "archive"
      ? archivedProjects
      : projects.filter((p) =>
          tab === "completed" ? p.status === "tamamlandi" : p.status !== "tamamlandi"
        );

  const handleSave = async () => {
    try {
      const payload = { ...form, units_count: Number(form.units_count) || 0, budget: Number(form.budget) || 0 };
      if (editingId) {
        await api.patch(`/projects/${editingId}`, payload);
        toast.success("Proje güncellendi");
      } else {
        await api.post("/projects", payload);
        toast.success("Proje oluşturuldu");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleArchive = async (id) => {
    if (!window.confirm("Bu projeyi arşive taşımak istediğinize emin misiniz?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Proje arşive taşındı");
    load();
  };

  const handleRestore = async (id) => {
    await api.post(`/projects/${id}/restore`);
    toast.success("Proje geri getirildi");
    load();
  };

  const handlePurge = async (id) => {
    if (!window.confirm("Bu projeyi KALICI olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.")) return;
    await api.delete(`/projects/${id}/purge`);
    toast.success("Proje kalıcı olarak silindi");
    load();
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">
            Yönetim
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Projeler
          </h1>
        </div>
        {hasPerm("projects.create") && (
          <Button data-testid="projects-new-btn" onClick={() => setOpen(true)} className="font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Yeni Proje
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="projects-tabs">
          <TabsTrigger value="active" data-testid="tab-active">
            Aktif ({projects.filter((p) => p.status !== "tamamlandi").length})
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">
            Tamamlanan ({projects.filter((p) => p.status === "tamamlandi").length})
          </TabsTrigger>
          <TabsTrigger value="archive" data-testid="tab-archive">
            <Archive className="w-3 h-3 mr-1" /> Arşiv ({archivedProjects.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((p) => (
          <Card
            key={p.id}
            data-testid={`project-card-${p.id}`}
            className="p-5 hover:shadow-sm shadow-none rounded-md group transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div
                className="w-10 h-10 rounded-md bg-secondary flex items-center justify-center cursor-pointer"
                onClick={() => navigate(`/project/${p.id}`)}
              >
                <Building2 className="w-5 h-5 text-brand" />
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold ${STATUS_TONE[p.status]}`}>
                  {STATUS_LABEL[p.status]}
                </span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`project-menu-${p.id}`}
                      className="p-1.5 rounded hover:bg-secondary opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {tab !== "archive" && hasPerm("projects.edit") && (
                      <DropdownMenuItem onClick={() => openEdit(p)} data-testid={`project-edit-${p.id}`}>
                        <Pencil className="w-4 h-4 mr-2" /> Düzenle
                      </DropdownMenuItem>
                    )}
                    {tab === "archive" && hasPerm("projects.edit") && (
                      <DropdownMenuItem onClick={() => handleRestore(p.id)} data-testid={`project-restore-${p.id}`}>
                        <RotateCcw className="w-4 h-4 mr-2" /> Geri Getir
                      </DropdownMenuItem>
                    )}
                    {tab === "archive" && hasPerm("projects.delete") && (
                      <DropdownMenuItem onClick={() => handlePurge(p.id)} data-testid={`project-purge-${p.id}`} className="text-destructive">
                        <Trash2 className="w-4 h-4 mr-2" /> Kalıcı Sil
                      </DropdownMenuItem>
                    )}
                    {tab !== "archive" && hasPerm("projects.delete") && (
                      <DropdownMenuItem onClick={() => handleArchive(p.id)} className="text-destructive">
                        <Trash2 className="w-4 h-4 mr-2" /> Arşive Taşı
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            <div
              className="cursor-pointer"
              onClick={() => navigate(`/project/${p.id}`)}
            >
              <div className="font-heading font-semibold text-lg tracking-tight mb-1">{p.name}</div>
              <div className="text-xs text-muted-foreground mb-4">{p.address || "Adres belirtilmedi"}</div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-muted-foreground">Bütçe</div>
                  <div className="tabular-nums font-medium mt-0.5">{formatTRY(p.budget)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Daire</div>
                  <div className="tabular-nums font-medium mt-0.5">{p.units_count || 0}</div>
                </div>
              </div>
            </div>
          </Card>
        ))}
        {visible.length === 0 && (
          <div className="col-span-full text-sm text-muted-foreground p-12 text-center border border-dashed border-border rounded-md">
            {tab === "archive" ? "Arşivde proje yok." : tab === "completed" ? "Tamamlanmış proje yok." : "Henüz proje eklenmedi."}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogContent data-testid="project-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingId ? "Proje Düzenle" : "Yeni Proje"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Ad</Label>
              <Input
                data-testid="project-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Örn: MMF Levent Rezidans"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Adres</Label>
              <Textarea
                data-testid="project-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Mahalle</Label>
                <Input
                  data-testid="project-mahalle"
                  value={form.mahalle}
                  onChange={(e) => setForm({ ...form, mahalle: e.target.value })}
                  placeholder="Örn: Levent"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Ada</Label>
                <Input
                  data-testid="project-ada"
                  value={form.ada}
                  onChange={(e) => setForm({ ...form, ada: e.target.value })}
                  placeholder="Örn: 1234"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Parsel</Label>
                <Input
                  data-testid="project-parsel"
                  value={form.parsel}
                  onChange={(e) => setForm({ ...form, parsel: e.target.value })}
                  placeholder="Örn: 56"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Toplam Daire Sayısı</Label>
                <Input
                  type="number"
                  min="0"
                  data-testid="project-units-count"
                  value={form.units_count}
                  onChange={(e) => setForm({ ...form, units_count: e.target.value })}
                  placeholder="Örn. 48"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Bütçe (₺)</Label>
                <MoneyInput
                  data-testid="project-budget"
                  value={form.budget}
                  onChange={(n) => setForm({ ...form, budget: n })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Durum</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="project-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planlama">Planlama</SelectItem>
                    <SelectItem value="insaat">İnşaat</SelectItem>
                    <SelectItem value="tamamlandi">Tamamlandı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Başlangıç</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Bitiş</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="project-save" onClick={handleSave} disabled={!form.name}>
              {editingId ? "Kaydet" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
